import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { notificationTypeConfig } from '../types';
import { useNotificationDetail } from '../hooks/useNotificationDetail';
import { useWebViewLoading } from '../../../shared/hooks';
import { postMessageToFlutter } from '../../../shared/services/bridge';

import './NotificationDetail.css';

/**
 * iOS風エラーモーダルコンポーネント（CupertinoAlertDialog準拠）
 */
interface ErrorModalProps {
  title: string;
  message: string;
  onClose: () => void;
}

const ErrorModal: React.FC<ErrorModalProps> = ({ title, message, onClose }) => (
  <div className="error-modal-overlay" onClick={onClose}>
    <div className="error-modal" onClick={(e) => e.stopPropagation()}>
      <h2 className="error-modal-title">{title}</h2>
      <div className="error-modal-content">
        <p>{message}</p>
      </div>
      <button className="error-modal-button" onClick={onClose}>
        OK
      </button>
    </div>
  </div>
);

/**
 * お知らせ詳細コンポーネント
 */
function NotificationDetail() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const accountId = searchParams.get('accountId') || undefined;
  const token = searchParams.get('token') || undefined;
  const { data: notification, isLoading, error, markAsRead } = useNotificationDetail(id || '', accountId, token);
  const [showErrorModal, setShowErrorModal] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'auto';
  }, []);

  useEffect(() => {
    // 注意: APIモードでは、詳細APIを取得した時点でサーバー側で既読化される
    // そのため、notification.status は常に 'read' となる
    // 一覧画面からの遷移時にFlutterへの通知（markAsRead、unreadCount）は
    // NotificationList.tsx の handleRead で実施済み
    // 直接URLアクセス時は、画面を閉じた時にFlutter側でrefreshされる
    if (id && notification && notification.status === 'unread') {
      markAsRead();
      postMessageToFlutter(`markAsRead:${id}`);
      // 未読件数通知は一覧画面で実施済みのため、ここでは行わない
    }
  }, [id, notification, markAsRead]);

  // エラー発生時にモーダル表示 & Flutter側ローディング解除
  useEffect(() => {
    if (error) {
      setShowErrorModal(true);
      // エラー時はcontentRefがDOMにアタッチされないため、
      // useWebViewLoadingのhideLoadingが呼ばれない。
      // 明示的にhideLoadingを送信してFlutter側のローディングを解除する。
      postMessageToFlutter('hideLoading');
    }
  }, [error]);

  // Flutter WebViewのローディング制御（データ取得完了時 & ページ再表示時）
  // コールバックrefを使用してDOMが実際に描画された後にhideLoadingを呼ぶ
  const contentRef = useWebViewLoading(isLoading);

  // モーダルを閉じたらブラウザバック
  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    window.history.back();
  };

  // ローディング中はFlutter側のオーバーレイに任せるため、空のコンテナを返す
  if (isLoading) {
    return <div className="notification-detail-container" />;
  }

  // エラー時はモーダル表示
  if (error || !notification) {
    return (
      <div className="notification-detail-container">
        {showErrorModal && (
          <ErrorModal
            title="エラー"
            message="通信エラーが発生しました。しばらくしてからもう一度お試しください。"
            onClose={handleCloseErrorModal}
          />
        )}
      </div>
    );
  }

  const typeConfig = notificationTypeConfig[notification.type];

  // 日時をフォーマット
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日 ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  };

  return (
    <main ref={contentRef} className="notification-detail-container">
      <div className="notification-detail-header">
        <span
          className="notification-type-badge"
          style={{ backgroundColor: typeConfig.color }}
        >
          {typeConfig.label}
        </span>
        <span className="notification-date">{formatDate(notification.createdAt)}</span>
      </div>

      <h1 className="notification-detail-title">{notification.title}</h1>

      <hr className="divider" />

      <div className="notification-detail-body">
        {notification.body.split('\n').map((line, index) => (
          <React.Fragment key={index}>
            {line}
            {index < notification.body.split('\n').length - 1 && <br />}
          </React.Fragment>
        ))}
      </div>
    </main>
  );
}

export default NotificationDetail;
